'use client';

import { useCallback, useState, useSyncExternalStore } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import {
  ACTIVE_LOCATION_CHANGED_EVENT,
  persistActiveLocation,
  readActiveLocationSlug,
} from '~/lib/tenant/active-location-cookie';

/** A venue the customer can switch to (see getBookableLocations). */
export interface VenueOption {
  id: string;
  name: string;
  nameAlt?: string | null;
  slug: string;
  city: string | null;
  cityAlt?: string | null;
  /** court_locations.access_mode: 'public' | 'code_only' | 'link_only'. */
  accessMode?: string;
}

interface UseVenuePickerOptions {
  /** Tenant UUID — namespaces the cookie per tenant. */
  accountId: string;
  venues: VenueOption[];
  /** Whether a visitor with no saved venue is prompted on this route. Only
   *  called in the browser. */
  shouldPromptFirstPick: (pathname: string) => boolean;
  /** False while the host can't yet tell which surface it is (the layout
   *  shell's mobile check is pending); suppresses the first-pick prompt so
   *  it never opens on a surface that is about to be swapped out. */
  ready?: boolean;
}

interface PickerState {
  /** Opened by the visitor (chip / venue bar), as opposed to the prompt. */
  isOpenedManually: boolean;
  /** The first-pick prompt was swiped away. Kept for the session so it doesn't
   *  re-open on every venue-aware navigation: asking once is a prompt, asking
   *  on every page is a nag. A reload asks again. */
  dismissedFirstPick: boolean;
}

// Every cookie writer (the /book inline picker, AdoptVenue on a payment page,
// a pick here) fires ACTIVE_LOCATION_CHANGED_EVENT, so it doubles as the
// store's change signal.
function subscribeToActiveLocation(onChange: () => void) {
  window.addEventListener(ACTIVE_LOCATION_CHANGED_EVENT, onChange);
  return () => window.removeEventListener(ACTIVE_LOCATION_CHANGED_EVENT, onChange);
}

const noopSubscribe = () => () => {};

/** Venue name/city in the active language (Arabic falls back to primary). */
export function useVenueLabel() {
  const { i18n } = useTranslation();
  const preferAlt = i18n.language === 'ar';

  return (venue: VenueOption) => ({
    name: (preferAlt && venue.nameAlt?.trim()) || venue.name,
    city: (preferAlt && venue.cityAlt?.trim()) || venue.city,
  });
}

/**
 * State shared by the native top-bar chip and the web venue picker. Picking a
 * venue writes the `book:last-location:<tenantId>` cookie and refreshes the
 * route so every location-aware server component (packages, memberships,
 * coaches, /book) re-renders scoped to the new choice.
 *
 * Code-only venues deep-link into `/book?location=<slug>` instead: the
 * code-prompt flow there writes the cookie once the code is accepted.
 */
export function useVenuePicker({
  accountId,
  venues,
  shouldPromptFirstPick,
  ready = true,
}: UseVenuePickerOptions) {
  const router = useRouter();
  const pathname = usePathname();
  const urlSlug = useSearchParams().get('location');

  const [state, setState] = useState<PickerState>({
    isOpenedManually: false,
    dismissedFirstPick: false,
  });

  // The server can't see the cookie the browser holds (it may differ after a
  // client-side write), so the first render treats it as unknown and the
  // prompt only ever appears after hydration.
  const isClient = useSyncExternalStore(noopSubscribe, () => true, () => false);
  const savedSlug = useSyncExternalStore(
    subscribeToActiveLocation,
    useCallback(() => readActiveLocationSlug(accountId), [accountId]),
    () => null,
  );

  const sortedVenues = [...venues].sort((a, b) => a.name.localeCompare(b.name));
  // The venue the page is rendered for, by the server's precedence: URL
  // `?location=` over the cookie. A stale slug (venue archived or renamed)
  // matches nothing, exactly as the server falls back to "no venue".
  const activeVenue =
    sortedVenues.find((venue) => venue.slug === urlSlug) ??
    sortedVenues.find((venue) => venue.slug === savedSlug) ??
    null;

  // Shown on routes whose content is per-venue until the visitor has a venue
  // or dismisses the prompt.
  const isFirstPickPrompt =
    ready &&
    isClient &&
    !activeVenue &&
    sortedVenues.length >= 2 &&
    !state.dismissedFirstPick &&
    shouldPromptFirstPick(pathname);

  const open = () => setState((prev) => ({ ...prev, isOpenedManually: true }));

  // Always honour a user-driven close (drag, scrim tap, Esc, back).
  const onOpenChange = (next: boolean) => {
    setState((prev) => ({
      isOpenedManually: next,
      dismissedFirstPick: prev.dismissedFirstPick || (!next && isFirstPickPrompt),
    }));
  };

  const pick = (venue: VenueOption) => {
    // A pick settles the prompt even when no cookie is written yet: a
    // code-only venue routes to /book, which must not re-prompt over the
    // code entry.
    setState({ isOpenedManually: false, dismissedFirstPick: true });

    if (venue.accessMode === 'code_only') {
      router.push(`/book?location=${encodeURIComponent(venue.slug)}`);
      return;
    }

    // A `?location=` in the URL outranks the cookie server-side, so it has to
    // go or the page would keep rendering the URL's venue.
    const params = new URLSearchParams(window.location.search);
    const hasUrlVenue = params.has('location');

    if (venue.slug === savedSlug && !hasUrlVenue) return;

    persistActiveLocation(accountId, venue.slug);

    if (hasUrlVenue) {
      params.delete('location');
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
      return;
    }

    // Re-runs server components on the current route with the new cookie.
    router.refresh();
  };

  return {
    venues: sortedVenues,
    activeVenue,
    isOpen: state.isOpenedManually || isFirstPickPrompt,
    needsInitialPick: isFirstPickPrompt,
    open,
    onOpenChange,
    pick,
  };
}
